import {
  type CollisionGrid,
  type Facing,
  facingFromStep,
  findPath,
  isAdjacentStep,
  type MoveRequestResult,
  type TilePosition,
  validateMoveRequest,
  walkStartClock,
} from '@shefira/shared';

/**
 * The local player's walk, predicted (ADR 0045).
 *
 * A click starts the walk here at once, along the path the server will walk
 * (same A*, same grid, same start) and at the cadence it will walk it
 * (`stepIntervalMs`). The server stays the only truth: every frame its tile is
 * compared with what this walk says it should be by now, and a disagreement
 * puts the walk back on the server's tile, gliding if the gap is small,
 * jumping if it is not. Nothing here decides anything but where the body is
 * drawn: distances, reach and fights are still judged on the server's tile.
 *
 * Pure and clock-free: every call takes `now`, so the tests drive it with a
 * simulated server and simulated latency.
 */

/** What the server says of the local player this frame. */
export interface ServerPosition extends TilePosition {
  /** The server's `anim` is `walk`: it still has tiles to go. */
  walking: boolean;
}

/** Where to draw the local player, and how. */
export interface PredictedFrame {
  /** Position in tiles, fractional between two tiles; the caller rounds it to pixels. */
  x: number;
  y: number;
  /** Facing of the last step taken, null before any. */
  facing: Facing | null;
  /** Crossing a tile or with tiles left: the walk cycle plays. */
  moving: boolean;
}

export interface MovementPredictionOptions {
  /**
   * How long the server may still show the player standing on a tile this
   * walk already left before it counts as stopped there. It must cover a
   * round trip and a step: with less, every walk would start with a
   * correction on a slow connection.
   */
  lagAllowanceMs?: number;
  /** Beyond this gap, in tiles, a correction jumps instead of gliding. */
  glideTiles?: number;
  /** Half-life of a glided correction, in milliseconds. */
  glideHalfLifeMs?: number;
}

const DEFAULT_LAG_ALLOWANCE_MS = 1000;
/**
 * Two tiles: a click that races a server step leaves the two walks one tile
 * apart, which on screen can be up to two (one ahead, one behind). A bigger
 * gap is a journey, a respawn or a real desync, better shown as a jump.
 */
const DEFAULT_GLIDE_TILES = 2;
const DEFAULT_GLIDE_HALF_LIFE_MS = 40;
/** Tiles left longer ago than this are forgotten; the server is never that late. */
const TRAIL_MAX_AGE_MS = 10_000;
const TRAIL_MAX_LENGTH = 128;

interface TrailEntry {
  tile: TilePosition;
  leftAt: number;
  /** Order of leaving: the server walks the same tiles in the same order. */
  seq: number;
}

function same(a: TilePosition, b: TilePosition): boolean {
  return a.x === b.x && a.y === b.y;
}

function copy(tile: TilePosition): TilePosition {
  return { x: tile.x, y: tile.y };
}

export class MovementPrediction {
  private readonly lagAllowanceMs: number;
  private readonly glideTiles: number;
  private readonly glideHalfLifeMs: number;

  /** The tile the walk has entered: what the server will say once it catches up. */
  private current: TilePosition | undefined;
  /** The tile it came from; the body is drawn between the two while it crosses. */
  private previous: TilePosition | undefined;
  /** Tiles still to walk, the current one excluded. */
  private path: TilePosition[] = [];
  private destination: TilePosition | undefined;
  private grid: CollisionGrid | undefined;
  /** When the current tile was entered, on the caller's clock. */
  private lastStepAt = Number.NEGATIVE_INFINITY;
  private intervalMs = 250;
  private facing: Facing | null = null;
  /** Tiles walked lately, so a server a round trip behind is not mistaken for a desync. */
  private trail: TrailEntry[] = [];
  private nextSeq = 0;
  /**
   * The server only goes forward along the trail: once seen on a tile, an
   * older one is not "late" any more but somewhere else (walking back into a
   * fight, say), and it is followed.
   */
  private serverSeq = 0;
  /**
   * The walk is the player's own (a click), not the server's walk followed
   * from its reports: a new click may then say where it turns.
   */
  private leading = false;
  /**
   * The tiles the old paths went on to when clicks turned the walk mid-step.
   * A click can reach a server that already stepped onto one; the server
   * then comes back to the turning tile (`validateMoveFrom`), and seeing it
   * there is no desync. `seq` is the turning tile's place in the trail.
   * Several when clicks follow each other faster than a round trip.
   */
  private overshoots: { tile: TilePosition; seq: number; at: number }[] = [];
  /** What is left of a glided correction, added to the drawn position. */
  private offset = { x: 0, y: 0 };
  private lastUpdateAt: number | undefined;
  private correctionCount = 0;

  constructor(options: MovementPredictionOptions = {}) {
    this.lagAllowanceMs = options.lagAllowanceMs ?? DEFAULT_LAG_ALLOWANCE_MS;
    this.glideTiles = options.glideTiles ?? DEFAULT_GLIDE_TILES;
    this.glideHalfLifeMs = options.glideHalfLifeMs ?? DEFAULT_GLIDE_HALF_LIFE_MS;
  }

  /** The tile the predicted walk stands on; where a new walk starts from. */
  get tile(): TilePosition | undefined {
    return this.current === undefined ? undefined : copy(this.current);
  }

  /**
   * Where a click now turns the walk, for the server to turn there too
   * (`move_to.from`); undefined when the walk follows the server's own.
   */
  get turningTile(): TilePosition | undefined {
    return this.leading ? this.tile : undefined;
  }

  /** Where the walk the player leads goes; undefined once the server took it over. */
  get target(): TilePosition | undefined {
    return this.leading && this.destination !== undefined ? copy(this.destination) : undefined;
  }

  /** Tiles still to walk after the one being crossed: none once the last is entered. */
  get stepsLeft(): number {
    return this.path.length;
  }

  /** How many times the walk was moved onto the server's tile (behind it or ahead). */
  get corrections(): number {
    return this.correctionCount;
  }

  /**
   * A click: validate the walk from the predicted tile exactly as the server
   * will from its own (`validateMoveRequest`) and, if it holds, start it.
   * The caller sends `move_to` only when the result is ok.
   */
  walk(grid: CollisionGrid, destination: TilePosition, now: number): MoveRequestResult {
    this.grid = grid;
    if (this.current === undefined) return { ok: false, reason: 'no-path' };
    const result = validateMoveRequest(grid, this.current, destination);
    if (!result.ok || result.path === undefined) return result;
    // Standing still: the server takes the first step on the tick the intent
    // arrives, so the walk starts now. Mid-step, the new path waits for the
    // current tile to be crossed, as the server's does.
    if (this.path.length === 0) {
      this.lastStepAt = walkStartClock(this.lastStepAt, now, this.intervalMs);
    }
    const abandoned = this.path[0];
    const path = result.path.slice(1).map(copy);
    if (abandoned !== undefined && (path[0] === undefined || !same(path[0], abandoned))) {
      this.overshoots.push({ tile: copy(abandoned), seq: this.nextSeq, at: now });
    }
    this.path = path;
    this.leading = true;
    this.destination = copy(destination);
    return result;
  }

  /**
   * The walk is over for the server (an attack, a pickup, a death): the tile
   * being crossed is finished, nothing after it. A walk the server then makes
   * on its own (into melee range, to a drop) is followed from its reports.
   */
  stop(): void {
    this.path = [];
    this.destination = undefined;
    this.leading = false;
  }

  /** `move_rejected`: back to where the server says the player stands. */
  reject(server: TilePosition, now: number): void {
    this.stop();
    if (this.current === undefined) {
      this.snap(server);
      return;
    }
    if (!same(server, this.current)) this.correct(now, { ...server, walking: false });
  }

  /** Put the body on the server's tile at once: a journey, a respawn, a new zone. */
  snap(server: TilePosition): void {
    this.current = copy(server);
    this.previous = copy(server);
    this.path = [];
    this.destination = undefined;
    this.leading = false;
    this.clearTrail();
    this.offset = { x: 0, y: 0 };
  }

  /**
   * One frame: reconcile with the server's tile, walk the steps that fell due,
   * and say where to draw the body.
   */
  update(now: number, server: ServerPosition, intervalMs: number): PredictedFrame {
    this.intervalMs = intervalMs;
    if (this.current === undefined) this.snap(server);
    this.decayOffset(now);
    this.reconcile(now, server);
    this.advance(now);
    this.forget(now);
    const base = this.basePosition(now);
    const current = this.current ?? server;
    const previous = this.previous ?? current;
    return {
      x: base.x + this.offset.x,
      y: base.y + this.offset.y,
      facing: this.facing,
      moving:
        this.path.length > 0 || (!same(previous, current) && now - this.lastStepAt < intervalMs),
    };
  }

  private reconcile(now: number, server: ServerPosition): void {
    const current = this.current;
    if (current === undefined) return;

    // Behind on the walk's own tracks: the server is a round trip late, which
    // is the normal case. Standing there for too long means it stopped.
    // Checked before the current tile: a walk that turns back crosses its
    // own tracks, and the server on the first crossing is still behind.
    const left = this.lastLeft(server);
    if (left !== undefined && (server.walking || now - left.leftAt <= this.lagAllowanceMs)) {
      this.serverSeq = left.seq;
      return;
    }
    if (same(server, current)) {
      this.serverSeq = this.nextSeq;
      return;
    }

    // One step past a turn: the click raced the server's step, and it
    // comes back (`validateMoveFrom`).
    this.overshoots = this.overshoots.filter((overshoot) => overshoot.seq >= this.serverSeq);
    const raced = this.overshoots.some(
      (overshoot) =>
        same(server, overshoot.tile) &&
        (server.walking || now - overshoot.at <= this.lagAllowanceMs),
    );
    if (raced) return;

    // Ahead on the path: the prediction fell behind (a frozen tab, a walk
    // that followed a correction). Catch up to the server.
    const ahead = this.path.findIndex((tile) => same(tile, server));
    if (ahead >= 0) {
      this.fastForward(now, ahead);
      return;
    }

    this.correct(now, server);
  }

  /**
   * The next time the walk left `tile` from where the server was last seen on
   * the trail. The server walks the trail in order, so a tile walked twice (a
   * turn back) is the earlier visit first: matching the later one would put
   * the tiles in between behind the server, and pull the body back.
   */
  private lastLeft(tile: TilePosition): TrailEntry | undefined {
    return this.trail.find((entry) => entry.seq >= this.serverSeq && same(entry.tile, tile));
  }

  private leave(tile: TilePosition, at: number): void {
    this.trail.push({ tile: copy(tile), leftAt: at, seq: this.nextSeq });
    this.nextSeq += 1;
  }

  private clearTrail(): void {
    this.trail = [];
    this.serverSeq = this.nextSeq;
    this.overshoots = [];
  }

  private fastForward(now: number, index: number): void {
    const current = this.current;
    if (current === undefined) return;
    this.correctionCount += 1;
    const before = this.drawnPosition(now);
    const skipped = this.path.splice(0, index + 1);
    const reached = skipped.at(-1) ?? current;
    const from = skipped.at(-2) ?? current;
    this.leave(current, now);
    for (const tile of skipped.slice(0, -1)) this.leave(tile, now);
    // The server stands on the tile reached: everything before is behind it.
    this.serverSeq = this.nextSeq;
    this.previous = copy(from);
    this.current = copy(reached);
    this.lastStepAt = now;
    this.facing = facingFromStep(from, reached) ?? this.facing;
    this.keepContinuous(now, before);
  }

  /**
   * The server is somewhere this walk never was: take its tile. If it is
   * still walking to our destination, the rest of its path is the one A*
   * finds from there — the same code on the same grid gives the same path.
   */
  private correct(now: number, server: ServerPosition): void {
    const current = this.current;
    if (current === undefined) return;
    this.correctionCount += 1;
    const before = this.drawnPosition(now);
    this.clearTrail();

    const destination = this.destination;
    const resumed =
      server.walking && destination !== undefined && this.grid !== undefined
        ? findPath(this.grid, server, destination)
        : null;
    this.path = resumed === null ? [] : resumed.slice(1).map(copy);
    if (resumed === null) this.destination = undefined;

    if (isAdjacentStep(current, server)) {
      // One tile away: walk that tile at the usual pace, as a step.
      this.previous = copy(current);
      this.facing = facingFromStep(current, server) ?? this.facing;
    } else {
      this.previous = copy(server);
    }
    this.current = copy(server);
    this.lastStepAt = now;
    this.keepContinuous(now, before);
  }

  /** Walk every step that fell due; several after a frozen frame. */
  private advance(now: number): void {
    while (this.path.length > 0 && now - this.lastStepAt >= this.intervalMs) {
      const next = this.path.shift();
      const current = this.current;
      if (next === undefined || current === undefined) break;
      if (!isAdjacentStep(current, next)) {
        this.path = [];
        break;
      }
      this.lastStepAt += this.intervalMs;
      this.leave(current, this.lastStepAt);
      this.previous = current;
      this.current = copy(next);
      this.facing = facingFromStep(current, next) ?? this.facing;
    }
  }

  private forget(now: number): void {
    while (this.trail.length > 0) {
      const oldest = this.trail[0];
      if (
        oldest === undefined ||
        (this.trail.length <= TRAIL_MAX_LENGTH && now - oldest.leftAt <= TRAIL_MAX_AGE_MS)
      ) {
        break;
      }
      this.trail.shift();
    }
  }

  /** Between the tile left and the tile entered, by how much of the step has passed. */
  private basePosition(now: number): TilePosition {
    const current = this.current ?? { x: 0, y: 0 };
    const previous = this.previous ?? current;
    const progress = Math.min(1, Math.max(0, (now - this.lastStepAt) / this.intervalMs));
    return {
      x: previous.x + (current.x - previous.x) * progress,
      y: previous.y + (current.y - previous.y) * progress,
    };
  }

  private drawnPosition(now: number): TilePosition {
    const base = this.basePosition(now);
    return { x: base.x + this.offset.x, y: base.y + this.offset.y };
  }

  /**
   * After a correction, the body stays where it was drawn and glides to the
   * new position; past `glideTiles` it jumps there.
   */
  private keepContinuous(now: number, before: TilePosition): void {
    const after = this.basePosition(now);
    const offset = { x: before.x - after.x, y: before.y - after.y };
    this.offset = Math.hypot(offset.x, offset.y) > this.glideTiles ? { x: 0, y: 0 } : offset;
  }

  private decayOffset(now: number): void {
    const elapsed = this.lastUpdateAt === undefined ? 0 : Math.max(0, now - this.lastUpdateAt);
    this.lastUpdateAt = now;
    if (this.offset.x === 0 && this.offset.y === 0) return;
    const keep = 0.5 ** (elapsed / this.glideHalfLifeMs);
    this.offset = { x: this.offset.x * keep, y: this.offset.y * keep };
    // Under a thirty-second of a tile, half a pixel: nothing left to see.
    if (Math.hypot(this.offset.x, this.offset.y) < 1 / 32) this.offset = { x: 0, y: 0 };
  }
}
